import React, { useCallback, useState } from "react";
import { change, Field, getFormValues } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { ReduxControls } from "rhombus-react";
import { required, requiredNotNull, isURL } from "../validators/validators";
import { noop } from "utils/common";
import { FEED_CREATION_FORM } from "forms/list";
import { fetchSpreadsheetSheets } from "reducers/actions/ContentFeedActions";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";

import "./FeedSource.css";

const SOURCE_TYPE_OPTIONS = [
	{ label: "Google Spreadsheet", value: "google-spreadsheet" },
];

const SOURCE_URL_TOOLTIP = "The Source URL is created automatically and cannot be changed";

function toSheetOptions(sheets) {
	if (!Array.isArray(sheets)) {
		return [];
	}
	return sheets.map(sheet => ({ label: sheet?.title, value: sheet?.id }));
}

function FeedSource({ isAssignBlankFlow }) {
	const dispatch = useDispatch();
	const formValues = useSelector(state => getFormValues(FEED_CREATION_FORM)(state));
	const sourceUrl = formValues?.sourceUrl;

	useTooltipRebuild(isAssignBlankFlow);

	const [sheetOptions, setSheetOptions] = useState([]);

	const handleSourceUrlBlur = useCallback(async () => {
		const trimmedSourceUrl = sourceUrl?.trim();
		if (!trimmedSourceUrl || isURL(trimmedSourceUrl)) {
			return;
		}

		try {
			const response = await dispatch(fetchSpreadsheetSheets(trimmedSourceUrl));

			const responseData = response?.payload?.data || {};
			const spreadsheetSheets = responseData?.sheets || [];

			const availableSheetOptions = toSheetOptions(spreadsheetSheets);

			if (availableSheetOptions.length > 0) {
				setSheetOptions(availableSheetOptions);
				dispatch(change(FEED_CREATION_FORM, "sheetName", availableSheetOptions[0]));
				dispatch(change(FEED_CREATION_FORM, "sourceType", SOURCE_TYPE_OPTIONS[0]));
			}
		} catch (error) {
			console.error("Failed to fetch spreadsheet sheets:", error);
			setSheetOptions([]);
		}
	}, [dispatch, sourceUrl]);

	return (
		<div className="feed-source-fields">
			<div className="feed-source-form-field">
				<Field
					name="sourceUrl"
					label="Source URL"
					component={ReduxControls.Input}
					required={!isAssignBlankFlow}
					validate={isAssignBlankFlow ? [] : [required, isURL]}
					placeholder="Source URL"
					onBlur={isAssignBlankFlow ? undefined : handleSourceUrlBlur}
					disabled={isAssignBlankFlow}
					{...(isAssignBlankFlow && {
						'data-tip': SOURCE_URL_TOOLTIP,
						'data-html': true,
						'data-place': 'top',
						'data-class': 'source-url-tooltip',
					})}
				/>
			</div>

			<div className="feed-source-config">
				<div className="form-field">
					<Field
						name="sourceType"
						component={ReduxControls.Select}
						options={SOURCE_TYPE_OPTIONS}
						isClearable={false}
						required={!isAssignBlankFlow}
						validate={isAssignBlankFlow ? [] : [requiredNotNull]}
						placeholder="Source type"
						onMenuScrollToBottom={noop}
						isDisabled={isAssignBlankFlow}
					/>
				</div>

				<div className="form-field">
					<Field
						name="sheetName"
						component={ReduxControls.Select}
						options={sheetOptions}
						isClearable={false}
						required={!isAssignBlankFlow}
						validate={isAssignBlankFlow ? [] : [requiredNotNull]}
						placeholder="Worksheet"
						onMenuScrollToBottom={noop}
						isDisabled={isAssignBlankFlow}
					/>
				</div>
			</div>
		</div>
	);
}

export default FeedSource;
